import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useRef, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { AddBtn } from "@/App/Route/Add";
import { TableData } from "@/App/Route/Table";
import { listCircuitRoutes } from "@/Api/circuit_routes";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Search } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { Label } from "@/components/ui/label";

export type TableDataProps = {
  id: number;
  id_circuit_plan: string;
  title: string;
  starts: string;
  drivers: string | null;
  routes: Array<{
    id: string;
    eta: string | null;
    plan: string;
    type: string;
    notes: string;
    route: string | null;
    timing: {
      latestAttemptTime: string | null;
      earliestAttemptTime: string | null;
      estimatedAttemptDuration: string | null;
    };
    address: {
      address: string;
      placeId: string | null;
      latitude: number;
      longitude: number;
      placeTypes: Array<string>;
      addressLineOne: string;
      addressLineTwo: string;
    };
    activity: string;
    barcodes: Array<string>;
    orderInfo: {
      products: Array<string>;
      sellerName: string | null;
      sellerOrderId: string | null;
      sellerWebsite: string | null;
    };
    recipient: {
      name: string | null;
      email: string | null;
      phone: string | null;
      externalId: string | null;
    };
    webAppLink: string;
    deliveryInfo: {
      state: string;
      attempted: boolean;
      photoUrls: Array<string>;
      succeeded: boolean;
      signeeName: string | null;
      attemptedAt: string | null;
      signatureUrl: string | null;
      attemptedLocation: string | null;
      recipientProvidedNotes: string | null;
      driverProvidedInternalNotes: string | null;
      driverProvidedRecipientNotes: string | null;
    };
    packageCount: number | null;
    packageLabel: number | null;
    stopPosition: number | null;
    trackingLink: string | null;
    etaNullReason: {
      url: string | null;
      reason: string;
      message: string;
    };
    placeInVehicle: string | null;
    circuitClientId: string | null;
    driverIdentifier: string | null;
    optimizationOrder: string;
    estimatedTravelDistance: number | null;
    estimatedTravelDuration: number | null;
    allowedDriversIdentifiers: Array<string>;
  }>;
  depot: string;
  optimization: string;
  distributed: number;
  writable: number;
  is_active: number;
  created_at: string;
  updated_at: string;
};

interface Filters {
  title?: string;
  start_date?: string;
  end_date?: string;
  page: number;
}

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [filters, setFilters] = useState<Filters>({ page: 1 });
  const searchInput = useRef<HTMLInputElement>(null);
  const { hasPermission } = usePermissions();

  const fetchData = async (params?: Partial<Filters>) => {
    setState("loading");
    const currentFilters = { ...filters, ...params };
    const { data } = await listCircuitRoutes({
      title: currentFilters.title,
      start_date: currentFilters.start_date,
      end_date: currentFilters.end_date,
      page: currentFilters.page,
    });
    const response = data?.response;
    setListData({
      data: response?.data,
      current_page: response?.current_page,
      last_page: response?.last_page,
      next_page_url: response?.next_page_url,
    });
    setFilters(currentFilters);
    setState("production");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onSearch = () => {
    const title = searchInput.current?.value || undefined;
    fetchData({ title, page: 1 });
  };

  const handleNext = () => {
    if (listData.current_page < listData.last_page) {
      fetchData({ page: filters.page + 1 });
    }
  };

  const handlePrevious = () => {
    if (filters.page > 1) {
      fetchData({ page: filters.page - 1 });
    }
  };

  const handleLastPage = () => {
    if (listData.current_page < listData.last_page) {
      fetchData({ page: listData.last_page });
    }
  };

  const handleDateFilter = () => {
    fetchData({ page: 1 });
  };

  return (
    <Main>
      <If condition={hasPermission("view-circuit-route-list")}>
        <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            Rotas
          </h2>
          <If condition={hasPermission("create-circuit-route")}>
            <AddBtn reloadData={() => fetchData({ page: 1 })} />
          </If>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex gap-2">
              <Input
                placeholder="Pesquisar por título"
                className="w-64"
                ref={searchInput}
                onKeyUp={(e) => e.key === "Enter" && onSearch()}
              />
              <Button
                size="icon"
                className={cn(colors.bg_primary)}
                onClick={onSearch}
              >
                <Search className="size-5" />
              </Button>
            </div>
            <div className="flex gap-2 items-end">
              <div className="space-y-1">
                <Label htmlFor="start_date" className="text-sm">
                  Data Inicial
                </Label>
                <Input
                  id="start_date"
                  type="date"
                  className="w-40"
                  value={filters.start_date || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      start_date: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="end_date" className="text-sm">
                  Data Final
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  className="w-40"
                  value={filters.end_date || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      end_date: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <Button
                className={cn(colors.bg_primary)}
                onClick={handleDateFilter}
              >
                Filtrar
              </Button>
            </div>
          </div>
          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <>
              <TableData dataApi={listData} updateFront={setListData} />
              {listData.last_page > 1 && (
                <Pagination
                  current_page={listData.current_page}
                  last_page={listData.last_page}
                  handleNext={handleNext}
                  handlePrevious={handlePrevious}
                  handleLastPage={handleLastPage}
                />
              )}
            </>
          )}
        </div>
      </If>
    </Main>
  );
};
